import { ReactNode } from 'react';
import { Id, toast, ToastOptions, UpdateOptions } from 'react-toastify';
import { isRibbonToastSticky, RibbonToastSeverity } from './ribbon-toast-severity';
import { RIBBON_TOAST_AUTO_CLOSE_MS, RIBBON_TOAST_STYLE } from './ribbon-toast-style';
import { RibbonToastView } from './RibbonToastView';

export type RibbonToastChildren = ReactNode | ((closeToast: () => void) => ReactNode);

export type ShowRibbonToastOptions = {
  severity?: RibbonToastSeverity;
  title?: string;
  eyebrow?: string | null;
  body?: string | null;
  /** Custom body when the toast is not a simple title/body message. */
  children?: RibbonToastChildren;
  actionLabel?: string | null;
  onAction?: () => void;
  /** When true, the action chip shows an open-in-new icon. */
  opensInNewTab?: boolean;
  /**
   * Auto-close override. Defaults to sticky (false) for error/warning,
   * otherwise {@link RIBBON_TOAST_AUTO_CLOSE_MS}.
   */
  autoClose?: number | false;
  toastId?: Id;
};

function resolveAutoCloseMs(
  severity: RibbonToastSeverity,
  autoClose: number | false | undefined,
): number | undefined {
  if (autoClose === false) {
    return undefined;
  }
  if (typeof autoClose === 'number') {
    return autoClose;
  }
  return isRibbonToastSticky(severity) ? undefined : RIBBON_TOAST_AUTO_CLOSE_MS;
}

function buildToastOptions(autoCloseMs: number | undefined, toastId?: Id): ToastOptions {
  return {
    icon: false,
    closeButton: false,
    // Progress lives inside the Paper; hide Toastify's outer bar.
    autoClose: autoCloseMs ?? false,
    hideProgressBar: true,
    style: RIBBON_TOAST_STYLE,
    toastId,
  };
}

function resolveChildren(
  children: RibbonToastChildren | undefined,
  closeToast: (() => void) | undefined,
): ReactNode {
  if (typeof children === 'function') {
    return children(() => closeToast?.());
  }
  return children;
}

/**
 * Shows a toast with the shared MUI Paper + severity ribbon chrome.
 */
export function showRibbonToast({
  severity = 'info',
  title,
  eyebrow = null,
  body = null,
  children,
  actionLabel = null,
  onAction,
  opensInNewTab = false,
  autoClose,
  toastId,
}: ShowRibbonToastOptions): Id {
  const autoCloseMs = resolveAutoCloseMs(severity, autoClose);

  return toast(
    ({ closeToast }) => (
      <RibbonToastView
        title={title}
        eyebrow={eyebrow}
        body={body}
        severity={severity}
        actionLabel={actionLabel}
        opensInNewTab={opensInNewTab}
        onAction={
          onAction
            ? () => {
                onAction();
                closeToast?.();
              }
            : undefined
        }
        onClose={closeToast}
        autoCloseMs={autoCloseMs}
      >
        {resolveChildren(children, closeToast)}
      </RibbonToastView>
    ),
    buildToastOptions(autoCloseMs, toastId),
  );
}

export type UpdateRibbonToastOptions = ShowRibbonToastOptions & {
  toastId: Id;
};

/**
 * Replaces an existing ribbon toast's content (e.g. persistent job progress).
 */
export function updateRibbonToast({
  toastId,
  severity = 'info',
  title,
  eyebrow = null,
  body = null,
  children,
  actionLabel = null,
  onAction,
  opensInNewTab = false,
  autoClose,
}: UpdateRibbonToastOptions): void {
  const autoCloseMs = resolveAutoCloseMs(severity, autoClose);

  const options: UpdateOptions = {
    render: ({ closeToast }) => (
      <RibbonToastView
        title={title}
        eyebrow={eyebrow}
        body={body}
        severity={severity}
        actionLabel={actionLabel}
        opensInNewTab={opensInNewTab}
        onAction={
          onAction
            ? () => {
                onAction();
                closeToast?.();
              }
            : undefined
        }
        onClose={closeToast}
        autoCloseMs={autoCloseMs}
      >
        {resolveChildren(children, closeToast)}
      </RibbonToastView>
    ),
    ...buildToastOptions(autoCloseMs, toastId),
  };

  toast.update(toastId, options);
}
